import { useState, useEffect, useMemo, useCallback } from 'react';
import './custom-event-text-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import TemplatesTable, { COLUMN_DEFS, OUTPUT_TYPE_MAP } from './components/TemplatesTable.jsx';
import TemplateDrawer from './components/TemplateDrawer.jsx';
import ActiveFilterBar from '../../../components/v2/ActiveFilterBar.jsx';
import Pagination from '../../../components/v2/Pagination.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import ColumnVisibilityToggle from '../../../components/v2/ColumnVisibilityToggle.jsx';
import TableSkeleton from '../../../components/v2/TableSkeleton.jsx';
import usePersistedColumns from '../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';
import useToast from '../../../hooks/useToast.js';
import {
  getSession, getCampusId, getUserId, getSessionId,
  getCampusList, getRoleWeight, getCampusConfig, updateSessionCampus,
} from '../../../services/session.js';
import {
  fetchTemplates, addTemplate, editTemplate, deleteTemplates,
} from './api/customEventTextApi.js';
import { exportTemplatesCsv } from '../../../services/csvExport.js';

/** All column keys */
const ALL_COLUMN_KEYS = COLUMN_DEFS.map((c) => c.key);
const DEFAULT_VISIBLE = ALL_COLUMN_KEYS;

/** Output type definitions with campus config flags */
const OUTPUT_TYPES = [
  { id: 0, name: 'Global Default', configKey: null },
  { id: 3, name: 'Mobile', configKey: 'app_active' },
  { id: 4, name: 'Text', configKey: 'text_messaging_active' },
  { id: 5, name: 'Voice', configKey: 'voice_active' },
  { id: 6, name: 'Email', configKey: 'email_active' },
  { id: 7, name: 'Pager', configKey: 'pager_active' },
];

/**
 * CustomEventTextPage — V2 orchestrator for alert text template management.
 */
const CustomEventTextPage = () => {
  // ---------- Session ----------
  const sessionCampusId = getCampusId();
  const campusList = useMemo(() => getCampusList() || [], []);
  const myRoleWeight = getRoleWeight();
  const canManage = myRoleWeight >= 80;

  // ---------- Data & Loading ----------
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campusId, setCampusId] = useState(sessionCampusId || 0);

  // ---------- Table State ----------
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sortColumn, setSortColumn] = useState('output_type_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();
  const [visibleColumns, handleColumnToggle] = usePersistedColumns('custom-event-text', DEFAULT_VISIBLE, ALL_COLUMN_KEYS);
  const [selectedIds, setSelectedIds] = useState([]);

  // ---------- Drawer ----------
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // ---------- Delete Confirm ----------
  const [deleteConfirm, setDeleteConfirm] = useState(null); // single template or { bulk: true, count }
  const [deleting, setDeleting] = useState(false);

  // ---------- Toast ----------
  const { toast, showToast, dismissToast } = useToast();

  // ---------- Session Params ----------
  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [campusId]);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTemplates(campusId);
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[CustomEventTextPage] Load error:', err);
      showToast('error', 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [campusId, showToast]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ---------- Campus Change ----------
  const handleCampusChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    setCampusId(val);
    updateSessionCampus(val);
    setSearch('');
    setFilters({});
    setPage(1);
    setSelectedIds([]);
  }, []);

  // ==========================================================================
  // ENRICHMENT
  // ==========================================================================
  const enrichedTemplates = useMemo(() =>
    templates.map((t) => ({
      ...t,
      output_type_name: OUTPUT_TYPE_MAP[t.output_type_id] || `Type ${t.output_type_id}`,
      text_format_display: t.text_format,
      field_count: (t.text_format || '').split(',').filter(Boolean).length,
    })),
    [templates]
  );

  // ==========================================================================
  // AVAILABLE OUTPUT TYPES (for Add drawer)
  // ==========================================================================
  const availableOutputTypes = useMemo(() => {
    const config = getCampusConfig() || {};
    const existingTypeIds = new Set(templates.map((t) => t.output_type_id));
    return OUTPUT_TYPES
      .filter((ot) => ot.configKey === null || !!config[ot.configKey])
      .filter((ot) => !existingTypeIds.has(ot.id));
  }, [templates, campusId]);

  // ==========================================================================
  // DATA PIPELINE: search → filter → sort → paginate
  // ==========================================================================
  const searchKeys = ['output_type_name', 'text_format'];

  const filteredData = useMemo(() => {
    let result = enrichedTemplates;

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((r) =>
        searchKeys.some((k) => String(r[k] || '').toLowerCase().includes(q))
      );
    }

    // Column filters
    Object.entries(filters).forEach(([col, vals]) => {
      if (vals && vals.length > 0) {
        result = result.filter((r) => vals.includes(String(r[col])));
      }
    });

    return result;
  }, [enrichedTemplates, search, filters]);

  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortColumn] ?? '';
      const bVal = b[sortColumn] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sortColumn, sortOrder]);

  const totalFiltered = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const safePage = Math.min(page, totalPages);
  const paginatedData = sortedData.slice((safePage - 1) * perPage, safePage * perPage);

  // Column labels for filter bar
  const columnLabels = useMemo(
    () => Object.fromEntries(COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );

  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  const handleSort = useCallback((col) => {
    setSortColumn((prev) => {
      if (prev === col) { setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc')); return col; }
      setSortOrder('asc');
      return col;
    });
  }, []);

  const handleFilterChange = useCallback((col, vals) => {
    setFilters((prev) => ({ ...prev, [col]: vals }));
    setPage(1);
  }, []);

  const handleRemoveFilter = useCallback((col) => {
    setFilters((prev) => { const next = { ...prev }; delete next[col]; return next; });
    setPage(1);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    setPage(1);
  }, []);

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  // Select
  const handleSelectRow = useCallback((id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }, []);

  const handleSelectAll = useCallback(() => {
    const pageIds = paginatedData.map((r) => r.template_id);
    const allSelected = pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  }, [paginatedData, selectedIds]);

  // Drawer
  const openAddDrawer = useCallback(() => {
    setEditingTemplate(null);
    setDrawerOpen(true);
  }, []);

  const openEditDrawer = useCallback((row) => {
    setEditingTemplate(row);
    setDrawerOpen(true);
  }, []);

  // Save
  const handleSave = useCallback(async (formData) => {
    const session = getSessionParams();

    if (formData.template_id) {
      // Edit
      const payload = {
        template_id: formData.template_id,
        campus_id: campusId,
        text_format: formData.text_format,
      };
      const oldPayload = {
        template_id: formData.template_id,
        campus_id: campusId,
        text_format: formData.oldTextFormat,
      };
      const result = await editTemplate(payload, oldPayload, session);
      if (result[0]) {
        setDrawerOpen(false);
        setEditingTemplate(null);
        loadTemplates();
        showToast('success', 'Template updated');
      } else {
        showToast('error', result[1] || 'Update failed');
      }
    } else {
      // Add
      const payload = {
        output_type_id: formData.output_type_id,
        campus_id: campusId,
        text_format: formData.text_format,
        modified_by: session.userId,
      };
      const result = await addTemplate(payload, session);
      if (result[0]) {
        setDrawerOpen(false);
        loadTemplates();
        showToast('success', 'Template added');
      } else {
        showToast('error', result[1] || 'Add failed');
      }
    }
  }, [campusId, getSessionParams, loadTemplates, showToast]);

  // Delete (single)
  const handleDeleteTemplate = useCallback((row) => {
    setDeleteConfirm(row);
  }, []);

  // Delete (bulk)
  const handleBulkDelete = useCallback(() => {
    setDeleteConfirm({ bulk: true, count: selectedIds.length });
  }, [selectedIds.length]);

  // Execute delete after confirm
  const executeDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    const session = getSessionParams();

    if (deleteConfirm.bulk) {
      const payload = selectedIds.map((id) => ({ template_id: id }));
      const result = await deleteTemplates(payload, session);
      if (result[0]) {
        setSelectedIds([]);
        loadTemplates();
        showToast('success', `${selectedIds.length} template${selectedIds.length > 1 ? 's' : ''} deleted`);
      } else {
        showToast('error', result[1] || 'Bulk delete failed');
      }
    } else {
      const result = await deleteTemplates([{ template_id: deleteConfirm.template_id }], session);
      if (result[0]) {
        setSelectedIds((prev) => prev.filter((id) => id !== deleteConfirm.template_id));
        loadTemplates();
        showToast('success', `${deleteConfirm.output_type_name || 'Template'} deleted`);
      } else {
        showToast('error', result[1] || 'Delete failed');
      }
    }

    setDeleting(false);
    setDeleteConfirm(null);
  }, [deleteConfirm, selectedIds, getSessionParams, loadTemplates, showToast]);

  // Export — bulk selection
  const handleBulkExport = useCallback(() => {
    const selected = sortedData.filter((r) => selectedIds.includes(r.template_id));
    if (selected.length) exportTemplatesCsv(selected, 'templates-selected.csv', visibleColumns);
  }, [sortedData, selectedIds, visibleColumns]);

  // Export — all filtered data (page-level button)
  const handleExportAll = useCallback(() => {
    if (sortedData.length) exportTemplatesCsv(sortedData, 'custom-event-text-templates.csv', visibleColumns);
  }, [sortedData, visibleColumns]);

  // ==========================================================================
  // RENDER HELPERS
  // ==========================================================================
  const renderBulkBar = () => {
    if (selectedIds.length === 0) return null;
    return (
      <div className="ct-bulk-bar">
        <span className="ct-bulk-bar__count">{selectedIds.length} selected</span>
        <button type="button" className="dp-btn dp-btn--cancel dp-btn--sm" onClick={() => setSelectedIds([])}>Deselect All</button>
        <button type="button" className="dp-btn dp-btn--cancel dp-btn--sm" onClick={handleBulkExport}>Export</button>
        <button type="button" className="dp-btn dp-btn--danger dp-btn--sm" onClick={handleBulkDelete}>Delete</button>
      </div>
    );
  };

  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
        <div className="ps-page ct-page">
          {/* Toast */}
          <Toast toast={toast} onDismiss={dismissToast} />

          {/* Page Header */}
          <div className="ps-page-header">
            <div className="ps-page-header__inner">
              <div className="ps-breadcrumb-row">
                <div className="ps-breadcrumb">
                  <span className="ps-breadcrumb__text">Settings</span>
                  <svg className="ct-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                  <span className="ps-breadcrumb__current">Custom Event Text</span>
                </div>

                <div className="ps-campus-row">
                  <label htmlFor="ct-campus-selector" className="ps-campus-label">Campus:</label>
                  {campusList.length > 1 ? (
                    <select
                      id="ct-campus-selector"
                      value={campusId}
                      onChange={handleCampusChange}
                      className="ps-campus-select"
                    >
                      {campusList.map((c) => (
                        <option key={c.campus_id || c.CampusId} value={c.campus_id || c.CampusId}>
                          {c.campus_name || c.CampusName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="ps-campus-name">{campusList[0]?.campus_name || campusList[0]?.CampusName || ''}</span>
                  )}
                </div>
              </div>

              <h1 className="ps-page-title">Templates</h1>
            </div>
          </div>

          {/* Controls Bar */}
          <div className="ct-content">
            <div className="ct-controls">
              <div className="ct-controls__left">
                <div className="ct-search">
                  <svg className="ct-icon-md ct-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search templates..."
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="ct-search__input"
                    aria-label="Search templates"
                  />
                </div>
              </div>
              <div className="ct-controls__right">
                <ColumnVisibilityToggle
                  columns={COLUMN_DEFS}
                  visibleColumns={visibleColumns}
                  onToggle={handleColumnToggle}
                />
                <button
                  type="button"
                  className="ct-btn ct-btn--secondary"
                  onClick={handleExportAll}
                  aria-label="Export templates"
                >
                  <svg className="ct-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                {canManage ? (
                  <button type="button" className="ct-btn ct-btn--primary" onClick={openAddDrawer}>
                    <svg className="ct-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    New Template
                  </button>
                ) : null}
              </div>
            </div>

          {/* Active Filters */}
          <ActiveFilterBar
            filters={filters}
            columnLabels={columnLabels}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
          />

          {/* Bulk Action Bar */}
          {renderBulkBar()}

          {/* Table */}
          {loading ? (
            <TableSkeleton />
          ) : (
            <TemplatesTable
              data={paginatedData}
              allData={enrichedTemplates}
              visibleColumns={visibleColumns}
              sortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
              filters={filters}
              onFilterChange={handleFilterChange}
              selectedIds={selectedIds}
              onSelectRow={handleSelectRow}
              onSelectAll={handleSelectAll}
              onEdit={openEditDrawer}
              onDelete={handleDeleteTemplate}
              onAdd={openAddDrawer}
              onClearFilters={handleClearAllFilters}
              canManage={canManage}
            />
          )}

          {/* Pagination */}
          {!loading && totalFiltered > 0 ? (
            <Pagination
              page={safePage}
              totalPages={totalPages}
              total={totalFiltered}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
              entityLabel={totalFiltered === 1 ? 'template' : 'templates'}
              selectId="custom-event-text-per-page"
              className="ct-pagination"
            />
          ) : null}
          </div>
        </div>

        {/* Drawer */}
        <TemplateDrawer
          isOpen={drawerOpen}
          onClose={() => { setDrawerOpen(false); setEditingTemplate(null); }}
          template={editingTemplate}
          onSave={handleSave}
          availableTypes={availableOutputTypes}
        />

        {/* Delete Confirmation Dialog */}
        {deleteConfirm ? (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}
            onClick={() => setDeleteConfirm(null)}
          >
            <div
              style={{ background: 'var(--color-bg-primary, #fff)', borderRadius: 'var(--radius-lg, 0.75rem)', boxShadow: 'var(--shadow-xl)', width: '100%', maxWidth: '440px', overflow: 'hidden' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ padding: 'var(--space-4) var(--space-5)', borderBottom: '1px solid var(--color-border)' }}>
                <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-danger)' }}>
                  {deleteConfirm.bulk ? `Delete ${deleteConfirm.count} Template${deleteConfirm.count > 1 ? 's' : ''}` : 'Delete Template'}
                </h3>
              </div>
              <div style={{ padding: 'var(--space-5)' }}>
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  {deleteConfirm.bulk
                    ? `Are you sure you want to delete ${deleteConfirm.count} template${deleteConfirm.count > 1 ? 's' : ''}? This action cannot be undone.`
                    : (<>Are you sure you want to delete the <strong>{deleteConfirm.output_type_name}</strong> template? This action cannot be undone.</>)
                  }
                </p>
              </div>
              <div style={{ padding: 'var(--space-4) var(--space-5)', display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
                <button className="dp-btn dp-btn--cancel" onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</button>
                <button className="dp-btn dp-btn--danger" onClick={executeDelete} disabled={deleting}>
                  {deleting ? 'Deleting\u2026' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default CustomEventTextPage;
